import { Link } from '@tanstack/react-router';
import { Building2, KeyRound, MoreHorizontal, ShieldPlus, type LucideIcon } from 'lucide-react';
import { useState } from 'react';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export interface BottomNavItem {
  to: '/' | '/tenants' | '/admins' | '/settings';
  label: string;
  // Libellé court sous l'icône (colonne de ~75 px sur iPhone SE)
  shortLabel?: string;
  icon: LucideIcon;
  exact?: boolean;
}

// Les 4 sections ont leur icône : Autres porte les pages secondaires
const others: { to: '/tenants/new' | '/admins/new' | '/settings/change-password'; label: string; icon: LucideIcon }[] = [
  { to: '/tenants/new', label: 'Nouveau tenant', icon: Building2 },
  { to: '/admins/new', label: 'Nouvel admin', icon: ShieldPlus },
  { to: '/settings/change-password', label: 'Changer le mot de passe', icon: KeyRound },
];

const itemClass = 'flex h-full flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors';

/**
 * Navigation du bas sous md (la colonne latérale prend le relais au-dessus).
 * Candidat refactor @logistech/ui-components (TD-028) : même composant dans dashboard-web, pwa-manager et pwa-driver.
 */
export const BottomNav = ({ items }: { items: BottomNavItem[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <nav
      aria-label="Navigation mobile"
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/90 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
    >
      <ul className="grid h-16 grid-cols-5">
        {items.map(({ to, label, shortLabel, icon: Icon, exact }) => (
          <li key={to}>
            <Link
              to={to}
              activeOptions={{ exact: Boolean(exact) }}
              className={cn(itemClass, 'data-[status=active]:text-brand-violet')}
            >
              <Icon className="h-5 w-5" aria-hidden />
              {shortLabel ?? label}
            </Link>
          </li>
        ))}
        <li>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className={cn(itemClass, 'w-full')}>
              <MoreHorizontal className="h-5 w-5" aria-hidden />
              Autres
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]" aria-describedby={undefined}>
              <SheetHeader>
                <SheetTitle>Autres</SheetTitle>
              </SheetHeader>
              <ul className="mt-4 space-y-1">
                {others.map(({ to, label, icon: Icon }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-muted"
                    >
                      <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </SheetContent>
          </Sheet>
        </li>
      </ul>
    </nav>
  );
};
